<script setup lang="ts">
import DeleteButton from "@/components/DeleteButton.vue";
import HandleQueryStatus from "@/components/HandleQueryStatus.vue";
import SaveButton from "@/components/SaveButton.vue";
import { ConflictError } from "@/gateways/apiClient";
import ManagementLayout from "@/layouts/ManagementLayout.vue";
import { formatDatetime } from "@/utils/toFormattedDatetime";
import { Trash } from "@iconoir/vue";
import { computed, ref } from "vue";
import {
  useCreateInvitation,
  useDeleteInvitation,
  useGetInvitations,
} from "./invitationsCrud";
import { userRoles } from "./roles";
import type { Invitation } from "./types";

const setupInvitation = (): Invitation => {
  return {
    id: crypto.randomUUID(),
    email: "",
    role: "Member",
  };
};

const newInvitation = ref(setupInvitation());

const {
  createInvitation,
  createInvitationStatus,
  error: createInvitationError,
} = useCreateInvitation({
  onSuccess: () => {
    newInvitation.value = setupInvitation();
  },
});

const isConflictError = computed(
  () => createInvitationError.value instanceof ConflictError,
);

const { invitations, getInvitationsStatus } = useGetInvitations();
const { deleteInvitation, resetDeleteInvitation, deleteInvitationStatus } =
  useDeleteInvitation();
</script>

<template>
  <ManagementLayout
    :breadcrumbs="[
      { label: 'Nutzer:innen', to: { name: 'users' } },
      { label: 'Einladungen' },
    ]"
  >
    <template #header>
      <h1>Einladungen</h1>
    </template>

    <form
      class="column box"
      @submit.prevent="
        createInvitation({
          ...newInvitation,
          email: newInvitation.email.toLowerCase(),
        })
      "
    >
      <div class="multi-columns">
        <div>
          <label for="email">E-Mail</label>
          <input
            id="email"
            v-model.trim="newInvitation.email"
            type="email"
            required
          />
        </div>
        <div>
          <label for="role">Rolle</label>
          <select id="role" v-model="newInvitation.role" name="role">
            <option
              v-for="[key, value] in Object.entries(userRoles)"
              :key="key"
              :value="key"
            >
              {{ value }}
            </option>
          </select>
        </div>
      </div>
      <section v-if="isConflictError" class="error">
        Diese E-Mail-Adresse wird bereits verwendet.
      </section>
      <div class="row row-end">
        <SaveButton
          :saving-status="isConflictError ? 'idle' : createInvitationStatus"
          >Einladung senden</SaveButton
        >
      </div>
    </form>

    <HandleQueryStatus :query-status="getInvitationsStatus">
      <table v-if="invitations && invitations.length > 0">
        <thead>
          <tr>
            <th>E-Mail</th>
            <th>Rolle</th>
            <th>Erstellt am</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="invitation in invitations" :key="invitation.id">
            <td>{{ invitation.email }}</td>
            <td>{{ invitation.role }}</td>
            <td>
              {{
                invitation.createdAt ? formatDatetime(invitation.createdAt) : ""
              }}
            </td>
            <td class="actions">
              <DeleteButton
                class="button button--tertiary"
                :deleting-status="deleteInvitationStatus"
                @initiated="resetDeleteInvitation()"
                @confirmed="deleteInvitation(invitation)"
              >
                <Trash />
                <span class="visually-hidden"> Löschen </span>
                <template #confirmation>
                  Möchtest du die Einladung für "{{ invitation.email }}"
                  wirklich widerrufen?
                </template>
              </DeleteButton>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else>Es wurden noch keine Einladungen verschickt.</p>
    </HandleQueryStatus>
  </ManagementLayout>
</template>
